import {
  removeWhiteFromImageData,
  type RemoveWhiteBgOptions,
} from "@/home/remove-white-bg";

/** RGB colors drawn by `character/template.ts` (guides + label column). */
const TEMPLATE_DARK_FILL: ReadonlyArray<[number, number, number]> = [
  [5, 8, 12],
  [15, 20, 25],
  [18, 26, 36],
  [26, 35, 50],
];

const TEMPLATE_LINE: ReadonlyArray<[number, number, number]> = [
  [42, 53, 72],
  [61, 79, 102],
];

const TEMPLATE_LABEL: ReadonlyArray<[number, number, number]> = [
  [110, 156, 255],
  [139, 156, 179],
];

export type StripTemplateArtifactsOptions = {
  removeWhite?: boolean;
  removeBlack?: boolean;
  /** Solid + dashed grid strokes and label chrome. */
  removeGuides?: boolean;
  white?: RemoveWhiteBgOptions;
};

const DEFAULT_OPTS: StripTemplateArtifactsOptions = {
  removeWhite: true,
  removeBlack: true,
  removeGuides: true,
};

function dist2(r: number, g: number, b: number, tr: number, tg: number, tb: number): number {
  const dr = r - tr;
  const dg = g - tg;
  const db = b - tb;
  return dr * dr + dg * dg + db * db;
}

function matchesPalette(
  r: number,
  g: number,
  b: number,
  palette: ReadonlyArray<[number, number, number]>,
  maxDist2: number,
): boolean {
  for (const [tr, tg, tb] of palette) {
    if (dist2(r, g, b, tr, tg, tb) <= maxDist2) return true;
  }
  return false;
}

export function stripTemplateArtifactsFromImageData(
  imageData: ImageData,
  options?: StripTemplateArtifactsOptions,
): void {
  const opts = { ...DEFAULT_OPTS, ...options };
  if (opts.removeWhite) {
    removeWhiteFromImageData(imageData, opts.white);
  }

  if (!opts.removeBlack && !opts.removeGuides) return;

  const fillTol2 = 32 * 32;
  const lineTol2 = 26 * 26;
  const labelTol2 = 34 * 34;
  const px = imageData.data;

  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]!;
    const g = px[i + 1]!;
    const b = px[i + 2]!;
    const a = px[i + 3]!;
    if (a === 0) continue;

    if (opts.removeBlack && matchesPalette(r, g, b, TEMPLATE_DARK_FILL, fillTol2)) {
      px[i + 3] = 0;
      continue;
    }

    if (opts.removeGuides) {
      if (matchesPalette(r, g, b, TEMPLATE_LINE, lineTol2)) {
        px[i + 3] = 0;
        continue;
      }
      if (matchesPalette(r, g, b, TEMPLATE_LABEL, labelTol2)) {
        px[i + 3] = 0;
      }
    }
  }
}

export async function stripTemplateArtifactsFromDataUrl(
  dataUrl: string,
  options?: StripTemplateArtifactsOptions,
): Promise<string> {
  const opts = { ...DEFAULT_OPTS, ...options };
  if (!opts.removeWhite && !opts.removeBlack && !opts.removeGuides) {
    return dataUrl;
  }

  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  stripTemplateArtifactsFromImageData(imageData, opts);
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
    img.src = src;
  });
}
